/*
 * E-mail au membre, ligne par ligne (facturation des consommateurs, envoi des décomptes) :
 * case « E-mail », choix du modèle, aperçu de l'e-mail réel (variables remplacées) et modification
 * pour ce membre uniquement (le modèle de CRM → Paramètres → Comptabilité ne change pas).
 */
import { useState } from 'react';
import { Eye, Pencil, RotateCcw } from 'lucide-react';
import {
  DIRECT_DEBIT_VARIABLES, DOCUMENT_VARIABLES, type DocumentMessage, MESSAGE_KINDS, MESSAGE_LABELS, type MessageKind, renderDocumentMessage,
} from './accounting';
import { ModalShell, inputClass } from './ui';

export interface MemberEmailChoice {
  /** E-mail envoyé au membre avec le PDF. */
  send: boolean;
  kind: MessageKind;
  /** Modèle choisi à la main : il ne suit plus le montant (facture / décompte). */
  kindTouched: boolean;
  /** Texte modifié pour ce membre (avec variables) ; null = modèle des paramètres. */
  override: DocumentMessage | null;
}

interface Props {
  title: string;
  email: string | null;
  choice: MemberEmailChoice;
  onChange: (patch: Partial<MemberEmailChoice>) => void;
  /** Modèles des paramètres. */
  templates: Record<MessageKind, DocumentMessage>;
  /** Valeurs des variables pour ce membre (numéro et montant : connus après la préparation). */
  vars: Record<string, string>;
  /** E-mail obligatoire (domiciliation : pré-notification du prélèvement). */
  sendLockedReason?: string | null;
  /** E-mail déjà parti : plus rien à choisir. */
  sentAt?: string | null;
  disabled?: boolean;
  /** Première période du membre (badge « nouveau »). */
  first?: boolean;
}

export function MemberEmailControls({ title, email, choice, onChange, templates, vars, sendLockedReason, sentAt, disabled, first }: Props) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const source: DocumentMessage = choice.override ?? templates[choice.kind];
  const rendered = renderDocumentMessage(source, vars);
  const locked = !!sendLockedReason;
  const inactive = disabled || !!sentAt;

  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-neutral-600">
      <label className={`flex items-center gap-1 ${locked || inactive ? '' : 'cursor-pointer'}`} title={sendLockedReason ?? undefined}>
        <input type="checkbox" checked={locked || choice.send} disabled={locked || inactive} onChange={e => onChange({ send: e.target.checked })}
          aria-label={`Envoyer l’e-mail à ${title}`} />
        E-mail
      </label>
      {first && <span className="px-1.5 py-px rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold">nouveau membre</span>}
      <select value={choice.kind} disabled={inactive} onChange={e => onChange({ kind: e.target.value as MessageKind, kindTouched: true, override: null })}
        aria-label={`Modèle d’e-mail pour ${title}`}
        className="px-1.5 py-0.5 text-[11px] border border-neutral-200 rounded-md bg-white disabled:opacity-50">
        {MESSAGE_KINDS.map(k => <option key={k} value={k}>{MESSAGE_LABELS[k]}</option>)}
      </select>
      {choice.override && <span className="text-[10px] font-semibold text-amber-700">modifié</span>}
      <button type="button" onClick={() => { setOpen(true); setEditing(false); }}
        className="flex items-center gap-1 text-neutral-500 hover:text-neutral-900">
        <Eye className="w-3.5 h-3.5" />Aperçu du mail
      </button>
      {sentAt && <span className="text-emerald-700">déjà envoyé</span>}

      {open && (
        <ModalShell
          title={`E-mail à ${title}`}
          subtitle={`${MESSAGE_LABELS[choice.kind]}${choice.override ? ' · modifié pour ce membre' : ''}`}
          onClose={() => setOpen(false)}
          width="max-w-2xl"
          zIndex="z-[90]"
          footer={(
            <>
              {choice.override && !inactive && (
                <button onClick={() => onChange({ override: null })} className="mr-auto flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs text-neutral-600 hover:bg-neutral-100">
                  <RotateCcw className="w-3.5 h-3.5" />Rétablir le modèle
                </button>
              )}
              {!inactive && (
                <button onClick={() => setEditing(v => !v)} className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-700 hover:bg-white">
                  {editing ? <Eye className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}{editing ? 'Voir l’aperçu' : 'Modifier pour ce membre'}
                </button>
              )}
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-700">Fermer</button>
            </>
          )}
        >
          {editing ? (
            <div className="space-y-2">
              <input value={source.subject} onChange={e => onChange({ override: { ...source, subject: e.target.value } })} placeholder="Objet"
                className={`${inputClass} border-neutral-200`} />
              <textarea rows={12} value={source.body} onChange={e => onChange({ override: { ...source, body: e.target.value } })}
                className={`${inputClass} border-neutral-200 font-mono text-[13px] leading-relaxed`} />
              <p className="text-[10px] text-neutral-400">
                Modifie uniquement l’e-mail de ce membre. Variables : {[...DOCUMENT_VARIABLES, ...(choice.kind === 'direct_debit' ? DIRECT_DEBIT_VARIABLES : [])].map(v => v.key).join(' · ')}.
              </p>
            </div>
          ) : (
            <div className="rounded-lg border border-neutral-200 overflow-hidden text-xs">
              <div className="px-3 py-2 bg-neutral-50 border-b border-neutral-100 space-y-0.5">
                <p><span className="text-neutral-500">À : </span><span className="text-neutral-900">{email ?? <span className="text-amber-700">pas d’adresse e-mail</span>}</span></p>
                <p><span className="text-neutral-500">Objet : </span><span className="font-medium text-neutral-900">{rendered.subject || '—'}</span></p>
                <p className="text-neutral-500">Pièce jointe : PDF du document</p>
              </div>
              <div className="px-3 py-3 text-[13px] leading-relaxed text-neutral-800 [&_a]:text-blue-700 [&_a]:underline"
                dangerouslySetInnerHTML={{ __html: rendered.body.replace(/\n/g, '<br>') }} />
              {!(locked || choice.send) && (
                <p className="px-3 py-2 border-t border-neutral-100 text-[11px] text-amber-700">« E-mail » n’est pas coché : cet e-mail ne partira pas.</p>
              )}
            </div>
          )}
        </ModalShell>
      )}
    </div>
  );
}
